/**
 * Sentry browser init for lexsite (marketing site).
 *
 * Shares the `lexiun-site` Sentry project with the Express API. Events
 * from both halves land in the same project and are filterable via
 * `runtime=browser` (here) vs `runtime=node` (server/instrument.ts).
 * See ai-docs/sentry-implementation-plan-DONE.md §C6.
 *
 * Contract documented in ai-docs/sentry-integration-guide.md. Do not
 * diverge from the scrubPII / IGNORE_ERRORS list without updating the
 * guide first.
 */

import * as Sentry from "@sentry/react";

const SERVICE_SLUG = "lexiun-site";
const GIT_SHA = (import.meta.env.VITE_GIT_SHA as string | undefined) ?? "unknown";
const LEXIUN_ENV = (import.meta.env.VITE_LEXIUN_ENV as string | undefined) ?? "development";
const DSN = (import.meta.env.VITE_SENTRY_DSN as string | undefined) ?? "";

if (!DSN && LEXIUN_ENV === "production") {
  // eslint-disable-next-line no-console
  console.error(`[${SERVICE_SLUG}] VITE_SENTRY_DSN missing in production — error tracking disabled`);
}

const REDACTED = "[redacted]";
const JWT_REGEX = /^eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const SENSITIVE_BODY_KEYS = new Set([
  "password",
  "token",
  "accessToken",
  "refreshToken",
  "secret",
  "apiKey",
  "api_key",
  "signingKey",
  "signing_key",
  "clerkSecretKey",
  "authorization",
]);
const MAX_STRING_LEN = 2048;

function redactKeysDeep(obj: unknown, keys: Set<string>): void {
  if (!obj || typeof obj !== "object") return;
  if (Array.isArray(obj)) {
    for (const item of obj) redactKeysDeep(item, keys);
    return;
  }
  const record = obj as Record<string, unknown>;
  for (const [k, v] of Object.entries(record)) {
    if (keys.has(k) || keys.has(k.toLowerCase())) {
      record[k] = REDACTED;
    } else if (typeof v === "string" && JWT_REGEX.test(v)) {
      record[k] = REDACTED;
    } else if (typeof v === "string" && v.length > MAX_STRING_LEN) {
      record[k] = `${v.slice(0, MAX_STRING_LEN)}…[truncated]`;
    } else if (v && typeof v === "object") {
      redactKeysDeep(v, keys);
    }
  }
}

function scrubPII(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (event.extra) redactKeysDeep(event.extra, SENSITIVE_BODY_KEYS);
  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (crumb.data) redactKeysDeep(crumb.data, SENSITIVE_BODY_KEYS);
    }
  }
  if (event.request?.url) {
    try {
      const u = new URL(event.request.url);
      for (const key of Array.from(u.searchParams.keys())) {
        if (SENSITIVE_BODY_KEYS.has(key.toLowerCase())) {
          u.searchParams.set(key, REDACTED);
        }
      }
      event.request.url = u.toString();
    } catch {
      // non-URL string — leave as-is
    }
  }
  return event;
}

const IGNORE_ERRORS: Array<string | RegExp> = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
  /^Non-Error promise rejection captured/,
  /NetworkError/,
  /AbortError/,
  /Failed to fetch/,
  "cancelled",
  // lexsite is a public marketing surface — ad-blockers / browser
  // extensions inject noise that isn't our code.
  /vid_mate_check/,
  /^ucbrowser/,
];

Sentry.init({
  dsn: DSN,
  release: `${SERVICE_SLUG}@${GIT_SHA}`,
  environment: LEXIUN_ENV,
  sendDefaultPii: false,
  tracesSampleRate: 0,
  debug: LEXIUN_ENV === "development",
  beforeSend: scrubPII,
  ignoreErrors: IGNORE_ERRORS,
});

Sentry.getGlobalScope().setTag("service", SERVICE_SLUG);
Sentry.getGlobalScope().setTag("runtime", "browser");

// Wouter breadcrumb hook — plan §C6: Wouter has no official Sentry
// integration, so we add a 5-line popstate listener that records
// navigation breadcrumbs manually. Works for back/forward; Wouter
// also uses history.pushState for links but since `popstate` doesn't
// fire on pushState we also hook history.pushState below.
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    Sentry.addBreadcrumb({ category: "navigation", message: location.pathname, level: "info" });
  });
  const origPush = history.pushState.bind(history);
  history.pushState = (...args) => {
    const result = origPush(...args);
    Sentry.addBreadcrumb({ category: "navigation", message: location.pathname, level: "info" });
    return result;
  };
}

// Window-scoped dedupe (see ai-docs/sentry-integration-guide.md §5.1).
const smokeFlag = "__lexiunSentrySmokeEmitted";
if (
  import.meta.env.VITE_SENTRY_VERIFY === "1" &&
  DSN &&
  typeof window !== "undefined" &&
  !(window as unknown as Record<string, unknown>)[smokeFlag]
) {
  (window as unknown as Record<string, unknown>)[smokeFlag] = true;
  Sentry.captureMessage(`${SERVICE_SLUG} startup-check`, "info");
  // eslint-disable-next-line no-console
  console.log(`[${SERVICE_SLUG}] VITE_SENTRY_VERIFY=1 — sent startup-check event`);
}
